import { keyBy } from 'lodash';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import {
  AuditCarrierDetail,
  AuditCarrierSummary,
  Rate,
} from '../../models/Rate';
import { AddressType } from '../../models/Terminal';
import rateService, {
  type BulkUpdateCoordinatesResult,
} from '../../services/rateService';

export const useRates = (carrierId?: number) => {
  const { data, isLoading, status } = useQuery(
    carrierId ? ['rates', carrierId] : 'rates',
    () => rateService.getAll(carrierId),
    {
      // reload the rates list on a schedule. This used to be every 5 seconds, but that's a lot of server load
      refetchInterval: 60000,
    }
  );

  return {
    isLoading,
    rates: data || [],
    status,
  };
};

export const useRateCreate = () => {
  const queryClient = useQueryClient();

  return useMutation<Rate, Error, Rate>(rateService.create, {
    onSuccess: (data) => {
      queryClient.setQueryData<Rate[] | undefined>(
        'rates',
        (rates?: Rate[]) => [...(rates || []), data]
      );

      queryClient.setQueryData<Rate[] | undefined>(
        ['rates', data.carrierId],
        (rates?: Rate[]) => [...(rates || []), data]
      );
    },
  });
};

export const useRatesCreate = () => {
  return useMutation<Rate[], Error, Rate[]>(rateService.bulkAdd);
};

export const useRateUpdateCoordinates = () => {
  const queryClient = useQueryClient();

  return useMutation<Rate, Error, number>(rateService.updateCoordinates, {
    onSuccess: (data) => {
      queryClient.setQueryData<Rate[] | undefined>('rates', (rates?: Rate[]) =>
        rates?.map((rate) => (rate.id === data.id ? data : rate))
      );

      queryClient.setQueryData<Rate[] | undefined>(
        ['rates', data.carrierId],
        (rates?: Rate[]) =>
          rates?.map((rate) => (rate.id === data.id ? data : rate))
      );
    },
  });
};

export const useRatesUpdateCoordinates = () => {
  const queryClient = useQueryClient();

  return useMutation<
    BulkUpdateCoordinatesResult,
    Error,
    [number[], number | undefined]
  >(([ids]) => rateService.bulkUpdateCoordinates(ids), {
    onSuccess: (_data, [_ids, carrierId]) => {
      queryClient.invalidateQueries(carrierId ? ['rates', carrierId] : 'rates');
    },
  });
};

export const useRateUpdate = () => {
  const queryClient = useQueryClient();

  return useMutation<Rate, Error, Rate>(rateService.update, {
    onSuccess: (data) => {
      queryClient.setQueryData<Rate[] | undefined>('rates', (rates?: Rate[]) =>
        rates?.map((rate) => (rate.id === data.id ? data : rate))
      );

      queryClient.setQueryData<Rate[] | undefined>(
        ['rates', data.carrierId],
        (rates?: Rate[]) =>
          rates?.map((rate) => (rate.id === data.id ? data : rate))
      );
      // marks this query as stale, triggers refetch
      queryClient.invalidateQueries(['costKmStats', data.carrierId]);

      // refetch rates if this updated rate has child rates
      const allRates =
        queryClient.getQueryData<Rate[]>('rates') ??
        queryClient.getQueryData<Rate[]>(['rates', data.carrierId]) ??
        [];
      const hasChildRates = allRates.some(
        (r: any) => (r.parent_rate_id ?? r.parentRateId) === data.id
      );
      if (hasChildRates) {
        queryClient.invalidateQueries('rates');
        queryClient.invalidateQueries(['rates', data.carrierId]);
      }
    },
  });
};

export const useRatesRemove = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, [number[], number | undefined]>(
    ([ids]) => rateService.bulkRemove(ids),
    {
      onSuccess: (data, [variables, carrierId]) => {
        if (!carrierId) {
          queryClient.setQueryData<Rate[] | undefined>(
            'rates',
            (rates?: Rate[]) =>
              rates?.filter((rate) => !variables.includes(rate.id))
          );
        } else {
          queryClient.setQueryData<Rate[] | undefined>(
            ['rates', carrierId],
            (rates?: Rate[]) =>
              rates?.filter((rate) => !variables.includes(rate.id))
          );
        }
      },
    }
  );
};

export const useRatesReverse = () => {
  const queryClient = useQueryClient();

  return useMutation<Rate[], Error, [number[], number | undefined]>(
    ([ids]) => rateService.bulkReverse(ids),
    {
      onSuccess: (data, [variables, carrierId]) => {
        if (!carrierId) {
          queryClient.setQueryData<Rate[] | undefined>(
            'rates',
            (rates?: Rate[]) => [...(rates || []), ...data]
          );
        } else {
          queryClient.setQueryData<Rate[] | undefined>(
            ['rates', carrierId],
            (rates?: Rate[]) => [...(rates || []), ...data]
          );
        }
      },
    }
  );
};

export const useRatesDuplicate = () => {
  const queryClient = useQueryClient();

  return useMutation<Rate[], Error, [number[], number | undefined]>(
    ([ids]) => rateService.bulkDuplicate(ids),
    {
      onSuccess: (data, [variables, carrierId]) => {
        if (!carrierId) {
          queryClient.setQueryData<Rate[] | undefined>(
            'rates',
            (rates?: Rate[]) => [...(rates || []), ...data]
          );
        } else {
          queryClient.setQueryData<Rate[] | undefined>(
            ['rates', carrierId],
            (rates?: Rate[]) => [...(rates || []), ...data]
          );
        }
      },
    }
  );
};

export const useRatesUpdate = () => {
  const queryClient = useQueryClient();

  return useMutation<Rate[], Error, [Rate[], number | undefined]>(
    ([rates]) => rateService.bulkUpdate(rates),
    {
      onSuccess: (data, [variables, carrierId]) => {
        const keydData = keyBy(data, (rate) => rate.id);
        if (!carrierId) {
          queryClient.setQueryData<Rate[] | undefined>(
            'rates',
            (rates?: Rate[]) =>
              rates?.map((rate) =>
                keydData[rate.id] ? keydData[rate.id] : rate
              )
          );
        } else {
          queryClient.setQueryData<Rate[] | undefined>(
            ['rates', carrierId],
            (rates?: Rate[]) =>
              rates?.map((rate) =>
                keydData[rate.id] ? keydData[rate.id] : rate
              )
          );
        }
      },
    }
  );
};

export const useRatesAssignTerminals = () => {
  const queryClient = useQueryClient();

  return useMutation<
    Rate[],
    Error,
    [number[], number[], AddressType, number | undefined]
  >(
    ([ids, terminals, type, carrierId]) =>
      rateService.assignTerminals(ids, terminals, type),
    {
      onSuccess: (data, [ids, terminals, type, carrierId]) => {
        const keydData = keyBy(data, (rate) => rate.id);
        if (!carrierId) {
          queryClient.setQueryData<Rate[] | undefined>(
            'rates',
            (rates?: Rate[]) =>
              rates?.map((rate) =>
                keydData[rate.id] ? keydData[rate.id] : rate
              )
          );
        } else {
          queryClient.setQueryData<Rate[] | undefined>(
            ['rates', carrierId],
            (rates?: Rate[]) =>
              rates?.map((rate) =>
                keydData[rate.id] ? keydData[rate.id] : rate
              )
          );
        }
      },
    }
  );
};

export const useRateAssignTerminals = () => {
  const queryClient = useQueryClient();

  return useMutation<
    Rate,
    Error,
    [number, number[], number[], number | undefined]
  >(
    ([id, originTerminals, destinationTerminals, carrierId]) =>
      rateService.singleAssignTerminals(
        id,
        originTerminals,
        destinationTerminals
      ),
    {
      onSuccess: (
        data,
        [id, originTerminals, destinationTerminals, carrierId]
      ) => {
        if (!carrierId) {
          queryClient.setQueryData<Rate[] | undefined>(
            'rates',
            (rates?: Rate[]) =>
              rates?.map((rate) => (data.id === rate.id ? data : rate))
          );
        } else {
          queryClient.setQueryData<Rate[] | undefined>(
            ['rates', carrierId],
            (rates?: Rate[]) =>
              rates?.map((rate) => (data.id === rate.id ? data : rate))
          );
        }
      },
    }
  );
};

export const useRatesAdjustBaseRate = () => {
  const queryClient = useQueryClient();

  return useMutation<
    Rate[],
    Error,
    [number[], number, string, number | undefined, string?]
  >(
    ([ids, amount, type, carrierId, note]) =>
      rateService.adjustBaseRates(ids, amount, type, note),
    {
      onSuccess: (_data, [_ids, _amount, _type, carrierId]) => {
        queryClient.invalidateQueries(
          carrierId ? ['rates', carrierId] : 'rates'
        );
      },
    }
  );
};

export const useRateAuditSummary = () => {
  const { data, error, isFetching, isLoading, refetch } = useQuery<
    AuditCarrierSummary[],
    Error
  >('rateAuditSummary', () => rateService.getAuditSummary(), {});

  return {
    auditSummary: data || [],
    error,
    isLoading: isLoading || isFetching,
    refetch,
  };
};

export const useRateAuditByCarrier = (carrierId: number | null) => {
  const { data, error, isFetching, isLoading, refetch } = useQuery<
    AuditCarrierDetail,
    Error
  >(
    ['rateAuditByCarrier', carrierId],
    () => rateService.getAuditByCarrier(carrierId!),
    {
      enabled: carrierId !== null,
    }
  );

  return {
    auditDetail: data,
    error,
    isFetching,
    isLoading,
    refetch,
  };
};

export const useMergeDuplicateRates = () => {
  const queryClient = useQueryClient();

  return useMutation<
    Rate,
    Error,
    {
      carrierId: number;
      origin: string;
      destination: string;
      shipTypeId: number;
      destinationShipTypeId: number;
      modeId: number;
      selectedRateClasses?: { [classId: number]: number };
      selectedRateId?: number | null;
    }
  >(
    (params) =>
      rateService.mergeDuplicateRates(
        params.carrierId,
        params.origin,
        params.destination,
        params.shipTypeId,
        params.destinationShipTypeId,
        params.modeId,
        params.selectedRateClasses,
        params.selectedRateId
      ),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries('rateAuditSummary');
        queryClient.invalidateQueries([
          'rateAuditByCarrier',
          variables.carrierId,
        ]);
      },
    }
  );
};
